import * as constant from "./constant";
import { fromJS } from "immutable";
import { message } from "antd";
import { mergeValues, isCurrentRoundDraft } from "utils/copyScore";
import { getSuggestScore } from "utils/suggest";

/**
 * 把 remainingScore 按权重比例整数分配给 items（Immutable Map 数组）。
 * 返回 { [id]: 整数 }，合计严格等于 remainingScore（任何路径都不留非 100）。
 *
 * 分配规则：
 * - 每项权重优先取「有效建议分」（> 0 的有限数字）；
 * - 建议分缺失（null/0）时回退用该项当前已填 value（>0），
 *   保证按既有相对占比分配，不会把已填项清成 0；
 * - 连 value 也没有（全 0/空，无任何参考依据）→ 均分兜底；
 * - 每项先按权重比例 Math.floor，剩余分用「最大余数法」分配：
 *   按理想值小数部分（ideal − floor）从大到小依次 +1，让每项结果尽量贴近理想比例，
 *   合计严格等于 remainingScore；
 * - 只剩一个未锁定项时，无论有无建议都把剩余分全部给它（组内合计仍为 100）；
 * - remainingScore ≤ 0（锁定项之和已达 100）→ 其余未锁定项一律清零。
 */
const distributeInt = (items, remainingScore, rounds, level) => {
  if (!items || items.length === 0) return {};

  const target = Math.max(0, Math.round(remainingScore));
  if (target === 0) {
    const result = {};
    items.forEach((it) => { result[it.get("id")] = 0; });
    return result;
  }

  // 权重 = 有效建议分优先，缺失时回退自身已填 value，保证组内合计仍可精确回到 100
  const weights = items.map((it) => {
    const s = Number(getSuggestScore(it, rounds, null, level));
    if (isFinite(s) && s > 0) return s;
    const v = Number(it.get("value"));
    return isFinite(v) && v > 0 ? v : 0;
  });
  const weightSum = weights.reduce((a, b) => a + b, 0);

  // 无任何参考依据时按均分处理理想值（各 ideal 相等，最大余数退化为前 r 项各 +1）
  const idealPer = weightSum > 0 ? null : target / items.length;

  const base = new Array(items.length).fill(0);
  const ideals = new Array(items.length).fill(0);
  for (let i = 0; i < items.length; i++) {
    const ideal =
      idealPer != null ? idealPer : (weights[i] / weightSum) * target;
    ideals[i] = ideal;
    base[i] = Math.floor(ideal);
  }

  // 最大余数法：小数部分（ideal − floor）最大的项优先 +1，直至余数分完
  const baseSum = base.reduce((a, b) => a + b, 0);
  const remainder = target - baseSum;
  const order = ideals
    .map((ideal, i) => ({ i, frac: ideal - base[i] }))
    .sort((a, b) => b.frac - a.frac);
  for (let k = 0; k < remainder; k++) {
    base[order[k].i] += 1;
  }

  const result = {};
  items.forEach((it, i) => { result[it.get("id")] = base[i]; });
  return result;
};

/**
 * 自动平衡同组：锁定当前项 → 剩余分数按建议比例分配给未锁定项。
 * 返回更新后的 state。
 */
const autoBalanceGroup = (state, tableId, level, factorId, rounds) => {
  const listPath = ["tablesMap", tableId, level];
  const list = state.getIn(listPath);
  if (!list || !list.size) return state;

  const idx = list.findIndex((i) => i.get("id") === factorId);
  if (idx === -1) return state;

  // tablesMap 被修改后统一同步 currentTableInfo，避免多 tab / autoSave 读到旧数据
  const syncCurrentTable = (st) =>
    st.get("activeTableId") === tableId
      ? st.set("currentTableInfo", st.getIn(["tablesMap", tableId]))
      : st;

  // 当前项设为锁定 + 清除 autoAdjusted
  let next = state
    .setIn([...listPath, idx, "locked"], true)
    .setIn([...listPath, idx, "autoAdjusted"], false);

  // 取最新 list 来筛选同组
  const latestList = next.getIn(listPath);
  const targetItem = latestList.get(idx);
  const groupItems = latestList.filter((it) => {
    if (it.get("status", 1) === 0) return false;
    if (level === "basic") return true;
    return it.get("parent_id") === targetItem.get("parent_id");
  });

  if (groupItems.size <= 1) return syncCurrentTable(next);

  // 计算锁定项之和
  let lockedSum = 0;
  groupItems.forEach((item) => {
    const v = item.get("value");
    if (item.get("locked") && v != null && v !== "") {
      lockedSum += Number(v);
    }
  });

  const remainingScore = Math.max(0, 100 - lockedSum);
  const unlockedItems = groupItems.filter((it) => !it.get("locked"));
  if (!unlockedItems.size) return syncCurrentTable(next);

  // 分配：无有效建议分时按已填 value 占比回退 / 均分兜底，
  // 保证所有未锁定项重新分配后组内合计 = 100（不会留下"校验未通过"状态）
  const distributions = distributeInt(
    unlockedItems.toArray(),
    remainingScore,
    rounds,
    level
  );

  // 应用分配结果
  let finalState = next;
  unlockedItems.forEach((item) => {
    const fid = item.get("id");
    const newVal = distributions[fid];
    const oldVal = item.get("value");
    const changed = Number(oldVal) !== Number(newVal);

    const sIdx = finalState
      .getIn(["tablesMap", tableId, level])
      .findIndex((i) => i.get("id") === fid);

    finalState = finalState.setIn(
      ["tablesMap", tableId, level, sIdx, "value"],
      newVal
    );

    // 仅当本次值确实被系统改动了才置 autoAdjusted=true（触发红色高亮）；
    // 本次值未变化时保留其原有 autoAdjusted 标记——之前被自动调整过的
    // 输入框保持红框，直到专家手动修改该项（手动修改时上方已清除标记）。
    if (changed) {
      finalState = finalState.setIn(
        ["tablesMap", tableId, level, sIdx, "autoAdjusted"],
        true
      );
    }
  });

  return syncCurrentTable(finalState);
};

const defaultState = fromJS({
  userInfo: {},
  modal: {
    isLogin: false,
    scoreDrawer: false,
    signModalVisible: false,
    copyPrompt: {
      visible: false,
      targetNames: [],
      targetIds: []
    }
  },
  currentPage: 0,
  scoreCard: {
    data: [],
    type: -1,
    parent: {
      basic: undefined,
      normal: undefined
    }
  },
  canLoadSave: false,
  // 每个评分表是否已询问过"加载自动保存内容"（tableId → true）
  autoSaveHandled: {},
  projectInfo: {
    tables: []
  },
  currentTableInfo: {},
  // 项目级多表 Tab 化：全部已加载表 + 当前激活表 id
  tablesMap: {},
  activeTableId: ""
});

export default (state = defaultState, action) => {
  switch (action.type) {
    case constant.CHANGE_ITEM:
      return state.setIn(action.target, action.value);

    case constant.CHANGE_FACTOR_VALUE:
      {
        // Step1. 设置scoreCard的data
        const scoreCardTargetIndex = state
          .getIn(["scoreCard", "data"])
          .findIndex((i) => i.get("id") === action.target.get("id"));

        state = state.setIn(
          ["scoreCard", "data", scoreCardTargetIndex, "value"],
          action.value
        );

        // Step2. 设置currentTable的value
        const targetIndex = state
          .getIn(["currentTableInfo", state.getIn(["scoreCard", "type"])])
          .findIndex((i) => i.get("id") === action.target.get("id"));

        state = state.setIn(
          [
            "currentTableInfo",
            state.getIn(["scoreCard", "type"]),
            targetIndex,
            "value"
          ],
          action.value
        );

        // Step3. 同步到 tablesMap[activeTableId]，保证多 tab 数据一致
        const activeTid = state.get("activeTableId");
        if (activeTid) {
          state = state.setIn(
            ["tablesMap", activeTid],
            state.get("currentTableInfo")
          );
        }
      }
      return state;

    case constant.TABLE_VALUE_CHANGE:
      {
        const { tableId, level, factorId, value } = action;
        const listPath = ["tablesMap", tableId, level];
        const list = state.getIn(listPath);
        if (!list || !list.size) return state;
        const idx = list.findIndex((i) => i.get("id") === factorId);
        if (idx === -1) return state;

        // 防御性：InputNumber 可能在清空/中间态时传 null/undefined/非数字
        // 这种情况只更新值，不触发自动平衡
        const isValid = value != null && value !== "" && isFinite(Number(value));

        state = state.setIn([...listPath, idx, "value"], isValid ? Number(value) : value);
        if (state.get("activeTableId") === tableId) {
          state = state.set(
            "currentTableInfo",
            state.getIn(["tablesMap", tableId])
          );
        }

        // 值无效则跳过自动平衡
        if (!isValid) return state;

        // 自动平衡同组
        const rounds = state.getIn(["projectInfo", "status"]) || 1;
        state = autoBalanceGroup(state, tableId, level, factorId, rounds);
      }
      return state;

    case constant.LOAD_ALL_TABLES:
      {
        let tablesMap = fromJS(action.tablesMap);

        // 已评分表是否直接恢复本地缓存，取决于"本会话是否刚提交过该表"
        // （autoSaveHandled 在 submitScore 成功时标记，页面刷新/重进即重置）：
        // - 同会话内提交后返回任务列表：直接恢复提交后的数据展示（有数据、不弹窗）；
        // - 退出页面再次进入：不自动回填（保持后端空白），改由
        //   "是否加载自动保存内容"弹窗确认（CHECK_AUTO_SAVE → LOAD_AUTO_SAVE）。
        const projectTables = state.getIn(["projectInfo", "tables"]) || [];
        projectTables.forEach((t) => {
          if (t.get("status")) {
            const tid = String(t.get("id"));
            if (!state.getIn(["autoSaveHandled", tid])) return;
            const raw = localStorage.getItem(tid);
            if (raw) {
              try {
                const saved = JSON.parse(raw);
                const savedTable = saved && saved.currentTableInfo;
                if (savedTable) {
                  tablesMap = tablesMap.set(
                    tid,
                    mergeValues(tablesMap.get(tid), fromJS(savedTable))
                  );
                }
              } catch (e) {
                // 本地缓存解析失败则忽略，保持后端数据
              }
            }
          }
        });

        const table = tablesMap.get(action.activeTableId);
        return state
          .set("tablesMap", tablesMap)
          .set("activeTableId", action.activeTableId)
          .set("currentTableInfo", table)
          .set(
            "scoreCard",
            fromJS({
              data: table.get("basic"),
              type: "basic"
            })
          );
      }

    case constant.SWITCH_TABLE:
      {
        const oldId = state.get("activeTableId");
        let next = state;
        // 旧表当前编辑态写回 tablesMap（兼容旧输入路径）
        if (oldId) {
          next = next.setIn(["tablesMap", oldId], state.get("currentTableInfo"));
        }
        const targetTable = next.getIn(["tablesMap", action.tableId]);
        if (!targetTable) return state;
        return next
          .set("activeTableId", action.tableId)
          .set("currentTableInfo", targetTable)
          .set(
            "scoreCard",
            fromJS({
              data: targetTable.get("basic"),
              type: "basic"
            })
          )
          .setIn(["modal", "scoreDrawer"], false);
      }

    case constant.CHANGE_SCORE_CARD:
      // 根据type判断
      if (["basic", "normal", "sub"].indexOf(action.source) !== -1) {
        // 点击面包屑的时候（因子层不操作）
        if (action.source === "basic") {
          return state
            .setIn(
              ["scoreCard", "data"],
              state.getIn(["currentTableInfo", "basic"])
            )
            .setIn(["scoreCard", "type"], "basic");
        } else if (action.source === "normal") {
          return state
            .setIn(
              ["scoreCard", "data"],
              state
                .getIn(["currentTableInfo", "normal"])
                .filter(
                  (i) =>
                    i.get("parent_id") ===
                    state.getIn(["scoreCard", "parent", "basic", "id"])
                )
            )
            .setIn(["scoreCard", "type"], "normal");
        } else {
          // 因子的时候不动它
          return state;
        }
      } else if (typeof action.source === "string") {
        // 点击评分卡跳转
        const [type, target] = action.source.split("-");

        if (type === "basic") {
          return state
            .set(
              "scoreCard",
              fromJS({
                data: state.getIn(["currentTableInfo", "basic"]),
                type: "basic"
              })
            )
            .setIn(["modal", "scoreDrawer"], false);
        } else if (type === "normal") {
          // 找到目标
          const targetNormal = state
            .getIn(["currentTableInfo", "normal"])
            .find((i) => i.get("id") === parseInt(target));

          const targetBasic = state
            .getIn(["currentTableInfo", "basic"])
            .find((i) => i.get("id") === targetNormal.get("parent_id"));

          return state
            .set(
              "scoreCard",
              fromJS({
                data: state
                  .getIn(["currentTableInfo", "normal"])
                  .filter((i) => i.get("parent_id") === targetBasic.get("id")),
                type: "normal",
                parent: {
                  basic: targetBasic
                }
              })
            )
            .setIn(["modal", "scoreDrawer"], false);
        } else {
          // 为sub的时候
          const targetSub = state
            .getIn(["currentTableInfo", "sub"])
            .find((i) => i.get("id") === parseInt(target));

          const targetNormal = state
            .getIn(["currentTableInfo", "normal"])
            .find((i) => i.get("id") === targetSub.get("parent_id"));

          const targetBasic = state
            .getIn(["currentTableInfo", "basic"])
            .find((i) => i.get("id") === targetNormal.get("parent_id"));

          return state
            .set(
              "scoreCard",
              fromJS({
                data: state
                  .getIn(["currentTableInfo", "sub"])
                  .filter((i) => i.get("parent_id") === targetNormal.get("id")),
                type: "sub",
                parent: {
                  basic: targetBasic,
                  normal: targetNormal
                }
              })
            )
            .setIn(["modal", "scoreDrawer"], false);
        }
      } else {
        // 展开下级的时候
        let targetType = "basic";
        let newScoreCardData = undefined;

        if (state.getIn(["scoreCard", "type"]) === "basic") {
          // 从normal中找数据
          targetType = "normal";
          newScoreCardData = state
            .getIn(["currentTableInfo", targetType])
            .filter((i) => i.get("parent_id") === action.source.get("id"));
        } else if (state.getIn(["scoreCard", "type"]) === "normal") {
          // 从sub中找数据
          targetType = "sub";
          newScoreCardData = state
            .getIn(["currentTableInfo", targetType])
            .filter((i) => i.get("parent_id") === action.source.get("id"));
        }

        return state
          .setIn(["scoreCard", "data"], newScoreCardData)
          .setIn(
            ["scoreCard", "parent", state.getIn(["scoreCard", "type"])],
            action.source
          )
          .setIn(["scoreCard", "type"], targetType);
      }

    /**
     * 自动保存
     */
    case constant.AUTO_SAVE:
      let tableId = state.getIn(["currentTableInfo", "id"]);
      // 仅当当前评分表确实填写过数值时才自动保存并提示"自动保存成功"；
      // 刚进入评分表（尚未填写任何数值）不触发保存、不弹提示，
      // 也不向 localStorage 写入"服务器刚加载的空数据"。
      const currentTable = state.get("currentTableInfo");
      const hasValues = ["basic", "normal", "sub"].some((type) => {
        const list = currentTable.get(type);
        return (
          list &&
          list.some(
            (f) => f.get("value") != null && f.get("value") !== ""
          )
        );
      });
      if (tableId && hasValues) {
        // 只存 currentTableInfo（多表模式下避免把整个 tablesMap 一起写入 localStorage），
        // 并记录当前轮次 rounds：用于区分"旧轮次数据"与"当前轮草稿"。
        // 第二轮进入时缓存里若还是第一轮数据（轮次对不上），不弹"是否加载自动保存内容"。
        localStorage.setItem(
          tableId,
          JSON.stringify({
            rounds: state.getIn(["projectInfo", "status"]),
            currentTableInfo: state.get("currentTableInfo").toJS()
          })
        );
        // 提交成功后静默更新缓存（autoSave(true)）不弹提示；仅用户操作保存时提示
        if (!action.silent) {
          message.success("自动保存成功");
        }
      }
      return state;

    case constant.LOAD_AUTO_SAVE: {
      // 仅用户点弹窗"是"触发：恢复该表自动保存内容，并标记该表已处理
      const tid = String(action.tableId);
      const localMarkData = localStorage.getItem(tid);
      if (state.get("canLoadSave") && localMarkData) {
        const saved = fromJS(JSON.parse(localMarkData));
        const savedTable = saved.get("currentTableInfo");

        // 已处理标记 + 关闭弹窗 + 重置抽屉/复制弹窗
        let next = state
          .set("canLoadSave", false)
          .setIn(["autoSaveHandled", tid], true)
          .setIn(["modal", "scoreDrawer"], false)
          .setIn(
            ["modal", "copyPrompt"],
            fromJS({ visible: false, targetNames: [], targetIds: [] })
          );
        if (!savedTable) {
          return next;
        }
        // 恢复自动保存：只把用户填写的 value 回填到当前表数据；
        // 建议分值（suggest_score / mean 等）是后端实时计算字段，
        // 一律保留 tablesMap 里的当前值，不随 localStorage 恢复。
        const currentTable = state.getIn(["tablesMap", tid]);
        const restoredTable = currentTable
          ? mergeValues(currentTable, savedTable)
          : savedTable;
        next = next.setIn(["tablesMap", tid], restoredTable);
        if (state.get("activeTableId") === tid) {
          next = next
            .set("currentTableInfo", restoredTable)
            .set(
              "scoreCard",
              fromJS({
                data: restoredTable.get("basic"),
                type: "basic"
              })
            );
        }
        return next;
      }
      return state;
    }

    case constant.CHECK_AUTO_SAVE: {
      // 进入页面 / 切换 tab 时检测：该表已处理过则不弹，
      // 有"当前轮次"的本地自动保存内容则弹"是否加载自动保存内容"。
      // 轮次判断（isCurrentRoundDraft）：缓存若还是旧轮次数据（如第二轮进入时
      // 缓存是第一轮提交的数据），与页面轮次对不上 → 不弹窗；
      // 用户修改后 autoSave 会把缓存更新为当前轮次 → 正常弹窗。
      const tid = String(action.tableId);
      if (state.getIn(["autoSaveHandled", tid])) {
        return state;
      }
      if (isCurrentRoundDraft(tid, state.getIn(["projectInfo", "status"]))) {
        return state.set("canLoadSave", true);
      }
      return state;
    }

    case constant.DISMISS_AUTO_SAVE: {
      // 用户点弹窗"否"：标记该表已处理（之后切回不再询问），不恢复数据
      const tid = String(action.tableId);
      return state
        .set("canLoadSave", false)
        .setIn(["autoSaveHandled", tid], true);
    }

    case constant.SHOW_COPY_PROMPT:
      return state.setIn(
        ["modal", "copyPrompt"],
        fromJS({
          visible: true,
          targetNames: action.targetNames,
          targetIds: action.targetIds
        })
      );

    case constant.COPY_CONFIRM:
    case constant.COPY_CANCEL:
      return state.setIn(
        ["modal", "copyPrompt"],
        fromJS({ visible: false, targetNames: [], targetIds: [] })
      );

    default:
      return state;
  }
};
